import React from "react";
import { cn } from "../../utils/cn";
import { Loader2 } from "lucide-react";

export const Button = React.forwardRef(({
  children,
  label,
  variant = "primary",
  size = "md",
  loading = false,
  disabled = false,
  icon: Icon,
  iconPos = "left",
  className,
  type = "button",
  ...props
}, ref) => {
  const baseStyles = "inline-flex items-center justify-center font-semibold transition-all duration-200 select-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none";

  const sizeStyles = {
    sm: "px-3 py-1.5 text-xs rounded-md gap-1.5",
    md: "px-4 py-2.5 text-sm rounded-lg gap-2",
    lg: "px-6 py-3 text-base rounded-xl gap-2.5",
  };

  const variantStyles = {
    primary: "bg-urb-primary text-black hover:bg-urb-primary-hover active:scale-[0.99] font-bold shadow-sm",
    secondary: "bg-urb-card-light text-urb-text hover:bg-urb-card-hover border border-urb-border",
    outline: "border border-urb-border text-urb-text hover:bg-urb-card-light hover:border-urb-primary hover:text-urb-primary",
    ghost: "text-urb-text-secondary hover:text-urb-text hover:bg-white/5",
    danger: "bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20",
    success: "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20",
  };

  const content = label || children;

  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={cn(
        baseStyles,
        sizeStyles[size] || sizeStyles.md,
        variantStyles[variant] || variantStyles.primary,
        className
      )}
      {...props}
    >
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin shrink-0" />
      ) : (
        Icon && iconPos === "left" && (
          typeof Icon === "function" || typeof Icon === "object" ? (
            <Icon className="w-4 h-4 shrink-0" />
          ) : (
            <i className={cn(Icon, "text-sm shrink-0")} />
          )
        )
      )}

      {content && <span>{content}</span>}

      {!loading && Icon && iconPos === "right" && (
        typeof Icon === "function" || typeof Icon === "object" ? (
          <Icon className="w-4 h-4 shrink-0" />
        ) : (
          <i className={cn(Icon, "text-sm shrink-0")} />
        )
      )}
    </button>
  );
});

Button.displayName = "Button";
export default Button;
